import { Localized } from "@fluent/react"
import { Alert, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography } from "@mui/material"
import { useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { deleteMailTemplate, selectMailTemplateById } from "./mailTemplatesSlice"
import { AppId } from "../../app/appTypes"
import { showSuccess } from "../notifications/notificationsSlice"
import { YesButton } from "../../app/components/YesButton"
import { CancelButton } from "../../app/components/CancelButton"

export interface RestoreTemplateDialogProps {
    id: AppId,
    onSaveSuccess : () => void
    onCancel  : () => void
}

export const RestoreTemplateDialog = ({ id, onSaveSuccess, onCancel }: RestoreTemplateDialogProps) => {
    const dispatch = useAppDispatch()
    const entity = useAppSelector(state => selectMailTemplateById(state, id ?? ""));
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [reason, setReason] = useState("")
    const [reasonError, setReasonError] = useState(false)
    
    const handleConfirmDelClick = async () => {
        if (!reason.trim()) {
            setReasonError(true)
            return
        }

        setSaving(true)
        try {
            if (entity) {
                const result = await dispatch(deleteMailTemplate({ id, reason: reason.trim() }))
                if (deleteMailTemplate.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    onSaveSuccess()
                } else {
                    let errorMsg = "error"
                    if (result.payload && result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
    
                        }
                    }
                    setServerError(errorMsg)
                }
            }
        }
        catch (error) {
            setServerError("error");
        }
        finally {
            setSaving(false)
        }
    }

    const handleCancelDelClick = () => {
        onCancel()
    }

    return <>
        <DialogTitle>
            <Localized id="mail-restore-system">
                <span>Przywróć systemowy</span>
            </Localized>
        </DialogTitle>
        <DialogContent>
            <DialogContentText>
                <Alert
                    severity="info"
                    variant="outlined"
                    sx={{
                        mb: 2,
                        mt: 1,
                        alignItems: "flex-start",
                        "& .MuiAlert-message": {
                            width: "100%",
                        },
                    }}
                >
                    <Typography variant="body2">
                        <Localized id="mail-restore-version-question">
                            <span>Czy na pewno chcesz przywrócić systemową wersję?</span>
                        </Localized>
                    </Typography>
                </Alert>
            </DialogContentText>
            <TextField
                fullWidth
                autoFocus
                required
                multiline
                minRows={2}
                value={reason}
                onChange={event => {
                    setReason(event.target.value)
                    if (reasonError && event.target.value.trim()) {
                        setReasonError(false)
                    }
                }}
                label={<Localized id="mail-change-reason-label">Powód zmiany</Localized>}
                error={reasonError}
                helperText={reasonError ? (
                    <Localized id="mail-change-reason-required">Podanie powodu jest wymagane.</Localized>
                ) : (
                    <Localized id="mail-change-reason-helper">Opisz powód przywrócenia szablonu.</Localized>
                )}
            />
            {serverError && (
                <Alert sx={{ marginTop: 1 }} severity="error">
                    <Localized id={serverError}>
                        <span style={{ whiteSpace: "pre-line" }}>{serverError}</span>
                    </Localized>
                </Alert>
            )}
        </DialogContent>
        <DialogActions>
            <YesButton loading={saving} onClick={handleConfirmDelClick}/>
            <CancelButton loading={saving} onClick={handleCancelDelClick}/>
        </DialogActions>
    </>
}
